import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ItemActions } from '../../utils/common.js'
import { type GridColumnWidth } from './index.js'
import { GridItem } from './GridItem.js'

const GRID_GAP = 4
const GRID_X_PADDING = 12
const GRID_REGULAR_MIN_COLUMN_WIDTH = 180
const GRID_LARGE_MIN_COLUMN_WIDTH = 300

export type InternalGridProps = {
  data: any[]
  fields: Record<any, any>
  virtualized?: boolean
  totalCount?: number
  onScroll?: (
    firstVisibleItemIndex: number,
    lastVisibleItemIndex: number,
    realFirstVisibleItemIndex: number,
    realLastVisibleItemIndex: number,
  ) => void
  select?: any
  onSelectChange?: (select?: any) => void
  onItemClick?: (item: any) => void
  itemActions?: ItemActions
  columnWidth?: GridColumnWidth
}

export const InternalGrid = ({
  data,
  fields: fieldsProp,
  virtualized,
  totalCount,
  onScroll,
  select,
  onSelectChange,
  onItemClick,
  itemActions,
  columnWidth = 'regular',
}: InternalGridProps) => {
  const fields = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fieldsProp).filter(([key, field]) =>
          Boolean(field.grid),
        ),
      ),
    [fieldsProp],
  )

  const total = totalCount ?? data.length
  const ref = useRef<HTMLDivElement>()
  const [columns, setColumns] = useState(0)
  const [width, setWidth] = useState(0)
  const [firstRow, setFirstRow] = useState(0)
  const height = useMemo(() => {
    const padding = 2 * 8
    const heightOfFields = (Object.values(fields).length - 1) * 24
    const heightOfImageField = width - padding + 4
    return padding + heightOfFields + heightOfImageField
  }, [width])

  useLayoutEffect(() => {
    if (!ref.current) return () => {}

    function handleScroll() {
      const { clientWidth, clientHeight, scrollTop } = ref.current
      const containerWidth = clientWidth - GRID_X_PADDING * 2
      const columns = Math.floor(
        containerWidth /
          ((columnWidth === 'large'
            ? GRID_LARGE_MIN_COLUMN_WIDTH
            : GRID_REGULAR_MIN_COLUMN_WIDTH) +
            GRID_GAP),
      )
      const width = Math.floor(
        (containerWidth - (columns - 1) * GRID_GAP) / columns,
      )
      const rows = Math.ceil(clientHeight / (height + GRID_GAP))
      const extra = columns
      const firstRow = Math.floor(scrollTop / (height + GRID_GAP))
      const firstRowWithOverscan = Math.max(0, firstRow - extra)
      const lastRow = firstRow + rows
      const lastRowWithOverscan = lastRow + extra

      setColumns(columns)
      setWidth(width)
      setFirstRow(firstRowWithOverscan)

      onScroll?.(
        firstRowWithOverscan * columns,
        lastRowWithOverscan * columns,
        firstRow * columns,
        lastRow * columns,
      )
    }

    handleScroll()
    ref.current.addEventListener('scroll', handleScroll)
    const resizeObserver = new ResizeObserver(handleScroll)
    resizeObserver.observe(ref.current)

    return () => {
      ref.current.removeEventListener('scroll', handleScroll)
      resizeObserver.disconnect()
    }
  }, [ref, height, onScroll, columnWidth])

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        display: 'grid',
        gap: GRID_GAP,
        gridTemplateColumns: `repeat(${columns}, minmax(0, ${width}px))`,
        padding: '8px 12px',
      }}
      ref={ref}
    >
      {virtualized &&
        Array.from({ length: columns }).map((_, i) => (
          <div
            key={'above' + i}
            style={{
              height: firstRow * (height + GRID_GAP),
            }}
          />
        ))}
      {data.map((item) => (
        <GridItem
          key={item.id}
          data={item}
          fields={fields}
          select={select}
          onSelectChange={onSelectChange}
          onClick={() => {
            onItemClick?.(item)
          }}
          itemActions={itemActions}
        />
      ))}
      {Array.from({
        length: (columns - (data.length % columns)) % columns,
      }).map((_, i) => (
        <div key={`padding` + i} />
      ))}
      {virtualized &&
        Array.from({ length: columns }).map((_, i) => (
          <div
            key={'below' + i}
            style={{
              height:
                ((total - firstRow * columns - data.length) / columns) *
                (height + GRID_GAP),
            }}
          />
        ))}
    </div>
  )
}
